import { useFlagConditionally } from '@mentimeter/flags/confidence/client';
import { useUser } from './useUser';

/**
 * Hook to determine if a user is an educational user.
 * This includes both free users with educational organization type from onboarding questions
 * and users with educational subscriptions.
 *
 * The feature is currently behind an A/B test flag 'is-education-plans-routing.enabled'.
 * If the user is identified as educational (either through subscription or organization type),
 * we check if they are in the test group before returning true.
 *
 * @param isEducational - Whether the user has an educational subscription
 * @param isFreeEducationalUser - Whether the user is a free user with educational organization type
 * @returns {boolean} True if the user is educational AND in the test group, false otherwise
 */
export function useIsEducationalUser(
  isEducational: boolean,
  isFreeEducationalUser: boolean,
): boolean {
  const { user } = useUser();
  const isFreeEducational = Boolean(user?.isFreeUser) && isFreeEducationalUser;
  const isEducationalUser = Boolean(isEducational || isFreeEducational);
  const { value: isEducationPlansRoutingEnabled } = useFlagConditionally(
    isEducationalUser,
    'is-education-plans-routing.enabled',
    false,
  );
  return isEducationPlansRoutingEnabled;
}
